/**
 * ADHÉSION EN LIGNE — /adhesion (univers DARK premium KAIZEN)
 *
 * - Choix du mode : Session 12 semaines (ponctuel) OU Abonnement aux 4 semaines.
 * - Cartes de forfaits → formulaire (nom/courriel/tél + mineur/parent) → Stripe Checkout.
 * - Retour Stripe sur /adhesion/merci?session_id=... → polling du statut → succès.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, CircleNotch, CheckCircle, ShieldCheck, ArrowLeft, Lock,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_WHITE = "/logos/master_text_sm.webp";
const ENSO = "/logos/kaizen_system/generated/kaizen_system_icon_only_enso_K.png";

const AGE_LABELS = {
  adulte: "Adulte", ado: "Ados", enfant: "Enfants", tous: "Tous âges",
};

export default function AdhesionPublic() {
  const sessionId = new URLSearchParams(window.location.search).get("session_id");
  if (sessionId) return <AdhesionMerci sessionId={sessionId} />;
  return <AdhesionChoix />;
}

/* ─────────────────────────── CHOIX DU FORFAIT ───────────────────────────── */
function AdhesionChoix() {
  const [plans, setPlans] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reco, setReco] = useState([]);
  const [pkhPlaces, setPkhPlaces] = useState(null);
  const [pkhWait, setPkhWait] = useState({ name: "", email: "", phone: "" });
  const [pkhWaitDone, setPkhWaitDone] = useState(false);
  const pkhAgent = (new URLSearchParams(window.location.search).get("pkh") || "").trim().toLowerCase();

  useEffect(() => {
    axios.get(`${API}/adhesion/plans`)
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => toast.error("Erreur de chargement des forfaits"));
    axios.get(`${API}/public/recommended-tariffs`)
      .then((r) => setReco(r.data.tariffs || []))
      .catch(() => {});
    axios.get(`${API}/public/powerkickher/places`)
      .then((r) => setPkhPlaces(r.data))
      .catch(() => {});
  }, []);

  const cycleLabel = (c) => ({ session: "/4 sem.", "4_semaines": "/4 sem.", mensuel: "/mois",
    trimestriel: "/3 mois", annuel: "/an", ponctuel: "", horaire: "/h", versements: "/mois" }[c] || "");

  return (
    <div className="kz-body min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="adhesion-page">
      <img src={ENSO} alt="" className="pointer-events-none absolute -right-32 -top-24 w-[520px] opacity-[0.04]" />

      {/* En-tête */}
      <header className="border-b border-white/10 px-5 md:px-12 py-7 flex items-center justify-between">
        <img src={LOGO_WHITE} alt="Institut Impact Élite Martial" className="h-12 object-contain" data-testid="public-master-logo" />
        <Link to="/univers" className="kz-mono text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-white transition-colors flex items-center gap-2" data-testid="back-univers-link">
          <ArrowLeft size={14} /> Retour
        </Link>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-12 py-12">
        <p className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#D90429]">Institut Impact Élite Martial · Val-d'Or</p>
        <h1 className="kz-display text-4xl md:text-6xl font-medium mt-3">Rejoins le dojo.</h1>
        <p className="kz-body text-white/50 mt-3 max-w-xl text-sm md:text-base">
          Choisis ton forfait, paie en ligne en toute sécurité, et accède immédiatement à ton Espace Membre.
        </p>

        {/* Facturation aux 4 semaines pour tous les contrats */}
        <p className="kz-body text-sm text-white/50 mt-7" data-testid="billing-note">
          Tous les forfaits sont facturés <b className="text-white">aux 4 semaines</b> (mensuel), annulables à tout moment.
          Le <b className="text-white">Forfait Découverte</b> est un essai unique de 2 semaines, sans engagement.
          <br />
          Des <b className="text-white">frais d'inscription de 40 $</b> (+ taxes) s'appliquent une seule fois à l'adhésion (re-payables lors d'un changement de programme : PCV → PCN → Masterclub).
        </p>

        {/* ⭐ Forfaits recommandés (source : Tarifs Dojo → Stripe) */}
        {reco.length > 0 && (
          <div className="mt-10" data-testid="reco-section">
            <div className="flex items-center gap-2 text-[#C9A961]">
              <span className="kz-mono text-[10px] tracking-[0.22em] uppercase">⭐ Nos forfaits recommandés</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 items-stretch" data-testid="reco-grid">
              {reco.map((t) => (
                <div key={t.id} data-testid={`reco-${t.id}`}
                  className="text-left border border-[#C9A961]/40 bg-[#C9A961]/[0.04] p-6 flex flex-col">
                  <span className="kz-mono text-[10px] tracking-[0.18em] uppercase text-white/40">{t.age_range || t.program_id}</span>
                  <h3 className="kz-display text-xl leading-tight mt-1.5">{t.label}</h3>
                  <div className="mt-auto pt-5">
                    <span className="kz-display text-3xl text-[#C9A961]">{Number(t.price).toLocaleString("fr-CA")} $</span>
                    <span className="kz-body text-white/40 text-xs"> {cycleLabel(t.billing_cycle)}</span>
                    <div className="kz-body text-[10px] text-white/30 mt-1">+ taxes (TPS/TVQ) au paiement</div>
                    {t.stripe_payment_url ? (
                      <a href={t.stripe_payment_url} target="_blank" rel="noopener noreferrer"
                        data-testid={`reco-pay-${t.id}`}
                        className="w-full mt-4 bg-[#C9A961] text-black py-3 font-bold tracking-wider hover:bg-white transition-colors flex items-center justify-center gap-2">
                        {t.stripe_mode === "subscription" ? "S'abonner" : "Payer"} <ArrowRight size={18} weight="bold" />
                      </a>
                    ) : (
                      <div className="w-full mt-4 border border-white/15 text-white/40 py-3 text-center text-xs">Lien à synchroniser</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 🥊 Bloc PowerKick Her — 100% femmes (3 parcours selon profil) */}
        {plans && plans.some((p) => p.discipline === "powerkickher") && (
          <div className="mt-10 border border-[#D90429]/40 bg-gradient-to-br from-[#D90429]/[0.08] to-transparent p-6 md:p-8" data-testid="powerkickher-block">
            <span className="kz-mono text-[10px] tracking-[0.22em] uppercase text-[#D90429]">🥊 100% femmes · 12 semaines · avec MEI</span>
            <h2 className="kz-display text-3xl md:text-4xl mt-2">PowerKick Her</h2>
            <p className="kz-body text-white/60 mt-2 max-w-xl text-sm">Le kickboxing au féminin. Reprends le contrôle de ton corps, ton énergie, ton bien-être — dans un groupe 100% femmes à Val-d'Or.</p>
            <div className="mt-3 inline-flex items-center gap-2 border border-[#C9A961]/50 bg-[#C9A961]/10 px-3 py-1.5" data-testid="pkh-price-guarantee">
              <span className="text-[#C9A961]">🔒</span>
              <span className="kz-mono text-xs text-[#C9A961]">Prix garanti jusqu'au 31 décembre — inscris-toi avant la hausse du 1er janvier</span>
            </div>
            {pkhPlaces && pkhPlaces.left > 0 && (
              <div className="mt-3 inline-flex items-center gap-2 border border-[#C9A961]/40 bg-[#C9A961]/10 px-3 py-1.5" data-testid="pkh-places">
                <span className="w-2 h-2 rounded-full bg-[#C9A961] animate-pulse" />
                <span className="kz-mono text-xs text-[#C9A961]">Plus que {pkhPlaces.left} place{pkhPlaces.left > 1 ? "s" : ""} pour la session en cours</span>
              </div>
            )}
            {pkhPlaces && pkhPlaces.left === 0 && (
              <div className="mt-3" data-testid="pkh-waitlist">
                {pkhWaitDone ? (
                  <div className="border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-emerald-300 text-sm" data-testid="pkh-waitlist-done">✓ Tu es sur la liste d'attente — on te contacte dès qu'une place se libère.</div>
                ) : (
                  <div className="border border-white/20 p-3">
                    <div className="kz-mono text-xs text-white/60 mb-2">Session complète — inscris-toi sur la liste d'attente :</div>
                    <div className="grid sm:grid-cols-3 gap-2">
                      <input value={pkhWait.name} onChange={(e) => setPkhWait((w) => ({ ...w, name: e.target.value }))} placeholder="Nom *" data-testid="pkh-wait-name" className="border border-white/20 bg-transparent px-3 py-2 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#D90429]" />
                      <input value={pkhWait.email} onChange={(e) => setPkhWait((w) => ({ ...w, email: e.target.value }))} placeholder="Courriel *" data-testid="pkh-wait-email" className="border border-white/20 bg-transparent px-3 py-2 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#D90429]" />
                      <input value={pkhWait.phone} onChange={(e) => setPkhWait((w) => ({ ...w, phone: e.target.value }))} placeholder="Téléphone" data-testid="pkh-wait-phone" className="border border-white/20 bg-transparent px-3 py-2 text-white placeholder-white/40 text-sm focus:outline-none focus:border-[#D90429]" />
                    </div>
                    <button onClick={async () => {
                        if (!pkhWait.name.trim() || !pkhWait.email.trim()) { toast.error("Nom et courriel requis"); return; }
                        try { await axios.post(`${API}/public/powerkickher/waitlist`, pkhWait); setPkhWaitDone(true); toast.success("Ajoutée à la liste d'attente ✓"); }
                        catch { toast.error("Échec — réessaie"); }
                      }} data-testid="pkh-wait-submit" className="mt-2 bg-[#D90429] text-white px-4 py-2 text-sm font-bold hover:bg-white hover:text-[#D90429] transition-colors">Rejoindre la liste d'attente</button>
                  </div>
                )}
              </div>
            )}
            <div className="kz-mono text-[11px] text-white/40 mt-4 mb-2 uppercase tracking-wide">Es-tu déjà membre ?</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <button onClick={() => {
                  const pk = plans.find((p) => p.discipline === "powerkickher");
                  if (!pk) return;
                  setSelected({ plan: pk, mode: "one_time", frequency: null, freqLabel: "Session 12 semaines", price: pk.price || 480, memberType: "nouvelle", pkhAgent });
                }}
                data-testid="pkh-nouvelle" className="text-left border border-[#D90429] bg-[#D90429] text-white p-4 hover:bg-white hover:text-[#D90429] transition-colors">
                <div className="font-bold">Nouvelle membre</div>
                <div className="text-xs opacity-80 mt-1">480 $ + tx · session 12 sem. · paiement en ligne → Espace Membre</div>
              </button>
              <Link to="/membres" data-testid="pkh-active" className="text-left border border-white/20 p-4 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors">
                <div className="font-bold text-white">Membre active</div>
                <div className="text-xs text-white/50 mt-1">J'ajoute PowerKick Her à mon forfait (sans re-payer l'inscription) → Espace Membre</div>
              </Link>
              <button onClick={() => {
                  const pk = plans.find((p) => p.discipline === "powerkickher");
                  if (!pk) return;
                  setSelected({ plan: pk, mode: "one_time", frequency: null, freqLabel: "Réactivation · session 12 sem.", price: pk.price || 480, memberType: "ancienne", pkhAgent, note: "Réactivation de ton dossier — ton profil se rouvre automatiquement après le paiement." });
                }}
                data-testid="pkh-ancienne" className="text-left border border-white/20 p-4 hover:border-[#C9A961] hover:text-[#C9A961] transition-colors">
                <div className="font-bold text-white">Ancienne membre</div>
                <div className="text-xs text-white/50 mt-1">Je réactive mon dossier + je paie ma session (480 $, sans frais d'inscription)</div>
              </button>
            </div>
          </div>
        )}

        {/* Grille de forfaits */}
        {!plans ? (
          <div className="flex items-center justify-center py-24">
            <CircleNotch size={32} className="text-[#D90429] animate-spin" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8 items-stretch" data-testid="plans-grid">
            {plans.filter((p) => p.discipline !== "powerkickher").map((p) => (
              <PlanCard key={p.id} p={p} onSelect={setSelected} />
            ))}
            <PetitsSamouraisCard />
          </div>
        )}

        <p className="kz-mono text-[10px] tracking-[0.16em] uppercase text-white/30 mt-8 flex items-center gap-2">
          <Lock size={13} /> Paiement sécurisé par Stripe · CAD · Aucune donnée de carte stockée
        </p>
      </div>

      {selected && (
        <CheckoutModal selection={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

/* ─────────────────────────── CARTE PETITS SAMOURAÏS (3-6 ans → page dédiée) ─────────────────── */
function PetitsSamouraisCard() {
  return (
    <div data-testid="plan-petits-samourais" className="flex flex-col border border-[#C9A961]/40 bg-[#C9A961]/5 p-6 hover:border-[#C9A961] transition-colors">
      <p className="kz-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A961]">Enfant · 3 à 6 ans · parent-enfant</p>
      <h3 className="kz-display text-2xl text-white mt-2">Petits Samouraïs Parents-Enfants</h3>
      <p className="kz-body text-white/50 text-sm mt-2">Initiation aux arts martiaux en duo parent-enfant : confiance, écoute et plaisir. Bloc de 8 semaines, places limitées.</p>
      <ul className="kz-body text-white/70 text-sm mt-4 space-y-1">
        <li className="flex justify-between border-b border-white/10 py-1"><span>Essai découverte duo (1 cours)</span><span className="kz-display">35 $</span></li>
        <li className="flex justify-between border-b border-white/10 py-1"><span>1 cours / semaine</span><span className="kz-display">131,97 $</span></li>
        <li className="flex justify-between border-b border-white/10 py-1"><span>2 cours / semaine</span><span className="kz-display">219,95 $</span></li>
      </ul>
      <div className="kz-body text-[10px] text-white/30 mt-2">Au 4 semaines · taxes incluses · frais d'inscription 35 $ au début de l'abonnement (jamais sur l'essai)</div>
      <div className="mt-auto pt-5">
        <Link to="/petits-samourais" data-testid="choose-petits-samourais" className="w-full bg-[#C9A961] text-black py-3 font-bold tracking-wider hover:bg-white transition-colors flex items-center justify-center gap-2">
          Inscrire mon enfant <ArrowRight size={18} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

/* ─────────────────────────── CARTE FORFAIT (fréquences) ─────────────────── */
function PlanCard({ p, onSelect }) {
  const isTrial = !!p.trial;
  const freqs = p.frequencies || [];
  const suggested = freqs.find((f) => f.suggested) || freqs[freqs.length - 1];
  const [freqKey, setFreqKey] = useState(suggested ? suggested.key : null);
  const chosen = freqs.find((f) => f.key === freqKey) || suggested;
  const price = isTrial ? p.price : (chosen ? chosen.price : 0);

  return (
    <div
      data-testid={`plan-${p.id}`}
      className={`text-left border bg-white/[0.02] p-6 flex flex-col ${isTrial ? "border-[#D90429]/50" : "border-white/10"}`}
    >
      <div className="flex items-center justify-between">
        <span className="kz-mono text-[10px] tracking-[0.18em] uppercase text-white/40">
          {AGE_LABELS[p.age_group] || p.age_group}
        </span>
        {isTrial && (
          <span className="kz-mono text-[9px] tracking-[0.14em] uppercase text-[#D90429] border border-[#D90429]/40 px-2 py-0.5">
            Essai · sans engagement
          </span>
        )}
      </div>
      <h3 className="kz-display text-xl leading-tight mt-1.5">{p.label}</h3>
      <p className="kz-body text-xs text-white/50 mt-2">{p.desc}</p>

      {/* Sélecteur de fréquence (1× / 2× / 3×) */}
      {!isTrial && (
        <div className="mt-4 space-y-1.5" data-testid={`freq-select-${p.id}`}>
          {freqs.map((f) => {
            const active = f.key === freqKey;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFreqKey(f.key)}
                data-testid={`freq-${p.id}-${f.key}`}
                className={`w-full flex items-center justify-between px-3 py-2 border transition-colors text-sm ${active ? "border-[#D90429] bg-[#D90429]/10" : "border-white/10 hover:border-white/30"}`}
              >
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${active ? "bg-[#D90429]" : "bg-white/20"}`} />
                  {f.label}
                  {f.suggested && (
                    <span className="kz-mono text-[8px] tracking-[0.12em] uppercase text-[#D90429] border border-[#D90429]/40 px-1.5 py-0.5">
                      Suggéré
                    </span>
                  )}
                </span>
                <span className="kz-display text-white/80">{Number(f.price).toLocaleString("fr-CA")} $</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-auto pt-5">
        <div>
          <span className="kz-display text-3xl text-[#D90429]">{Number(price).toLocaleString("fr-CA")} $</span>
          <span className="kz-body text-white/40 text-xs">{isTrial ? ` /${p.weeks} sem.` : " /4 sem."}</span>
          {!isTrial && (
            <div className="kz-body text-[10px] text-white/30 mt-1" data-testid={`inscription-note-${p.id}`}>
              + 40 $ frais d'inscription (une fois)
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => onSelect({ plan: p, mode: isTrial ? "one_time" : "recurring", frequency: isTrial ? null : freqKey, freqLabel: chosen ? chosen.label : "", price })}
          data-testid={`choose-${p.id}`}
          className="w-full mt-4 bg-[#D90429] text-white py-3 font-bold tracking-wider hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-2"
        >
          {isTrial ? "Commencer l'essai" : "S'inscrire"} <ArrowRight size={18} weight="bold" />
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────── FORMULAIRE / CHECKOUT ──────────────────────── */
function CheckoutModal({ selection, onClose }) {
  const { plan, mode, price, frequency, freqLabel, memberType, note, pkhAgent } = selection;
  const _params = new URLSearchParams(window.location.search);
  const [form, setForm] = useState({
    customer_name: _params.get("name") || "",
    customer_email: _params.get("email") || "",
    customer_phone: "",
    is_minor: false, parent_name: "",
  });
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function pay(e) {
    e?.preventDefault();
    if (!form.customer_name.trim() || !form.customer_email.trim()) {
      return toast.error("Nom et courriel requis");
    }
    if (form.is_minor && !form.parent_name.trim()) {
      return toast.error("Nom du parent requis pour un mineur");
    }
    if (!accepted) {
      return toast.error("Tu dois lire et accepter le contrat et les règles avant de payer.");
    }
    if (!waiverAccepted) {
      return toast.error("Tu dois signer la décharge de responsabilité avant de payer.");
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/adhesion/checkout`, {
        plan_id: plan.id,
        frequency,
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim(),
        customer_phone: form.customer_phone.trim(),
        is_minor: form.is_minor,
        parent_name: form.parent_name.trim(),
        terms_accepted: true,
        terms_accepted_at: new Date().toISOString(),
        waiver_accepted: true,
        waiver_accepted_at: new Date().toISOString(),
        origin_url: window.location.origin,
        member_type: memberType || "",
        pkh_agent: pkhAgent || "",
      });
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        throw new Error("Pas d'URL de paiement");
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Erreur — réessaie");
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={onClose} data-testid="checkout-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={pay}
        className="bg-[#0A0A0A] border border-white/15 w-full max-w-md p-8 max-h-[92vh] overflow-y-auto">
        <p className="kz-mono text-[10px] tracking-[0.22em] uppercase text-[#D90429]">Finaliser mon adhésion</p>
        <h3 className="kz-display text-2xl mt-1 leading-tight">{plan.label}</h3>
        {freqLabel && (
          <p className="kz-mono text-[11px] tracking-[0.12em] uppercase text-white/50 mt-1" data-testid="checkout-freq">{freqLabel}</p>
        )}
        {note && (
          <p className="kz-body text-[12px] text-[#C9A961] mt-2 border border-[#C9A961]/30 bg-[#C9A961]/5 px-3 py-2" data-testid="checkout-note">{note}</p>
        )}
        <div className="kz-display text-3xl text-[#D90429] mt-2">
          {Number(price).toLocaleString("fr-CA")} $
          <span className="kz-body text-white/40 text-sm">{mode === "recurring" ? " /4 sem." : ` /${plan.weeks} sem.`}</span>
        </div>
        {mode === "recurring" && (
          <div className="mt-1.5 border border-[#C9A961]/30 bg-[#C9A961]/5 px-3 py-2" data-testid="checkout-kien-fee">
            <p className="kz-body text-[12px] text-[#C9A961]">+ Frais d'inscription : <b>40 $</b> + taxes <span className="text-white/40">(une seule fois à la 1re inscription)</span></p>
            <p className="kz-body text-[10px] text-white/35 mt-0.5">Mêmes frais (40 $ + taxes) si retour après plus de 90 jours sans abonnement. Le détail exact et les taxes (TPS/TVQ) apparaissent au paiement Stripe et sur ton reçu.</p>
          </div>
        )}

        <div className="mt-6 space-y-4">
          <Input label="Nom complet *" value={form.customer_name} onChange={(v) => set("customer_name", v)} testid="checkout-name" />
          <Input label="Courriel *" type="email" value={form.customer_email} onChange={(v) => set("customer_email", v)} testid="checkout-email" />
          <Input label="Téléphone" value={form.customer_phone} onChange={(v) => set("customer_phone", v)} testid="checkout-phone" />
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={form.is_minor} onChange={(e) => set("is_minor", e.target.checked)} data-testid="checkout-minor" className="w-4 h-4 accent-[#D90429]" />
            Le membre est mineur (moins de 18 ans)
          </label>
          {form.is_minor && (
            <Input label="Nom du parent / tuteur *" value={form.parent_name} onChange={(v) => set("parent_name", v)} testid="checkout-parent" />
          )}
        </div>

        {/* ── LOT 3 — Contrat & règles à lire + accepter AVANT paiement ── */}
        <div className="mt-6 border border-white/15 bg-white/[0.03]" data-testid="checkout-terms-box">
          <p className="kz-mono text-[10px] tracking-[0.18em] uppercase text-[#D90429] px-4 pt-4">Contrat & règles du dojo</p>
          <div className="px-4 pb-3 pt-2 max-h-44 overflow-y-auto text-[12px] leading-relaxed text-white/70 space-y-2" data-testid="checkout-terms-text">
            <p><strong className="text-white/90">Engagement & paiement.</strong> {mode === "recurring"
              ? "Mon adhésion est facturée toutes les 4 semaines via Stripe. Je maintiens un mode de paiement valide."
              : `Je paie en un versement unique de ${Math.round(price)} $ + taxes pour la session complète de ${plan.weeks} semaines. Aucun abonnement récurrent, aucun renouvellement automatique.`}</p>
            <p><strong className="text-white/90">Annulation.</strong> {mode === "recurring"
              ? "L'arrêt d'un abonnement requiert un préavis écrit de 4 semaines par courriel à la réception. Des frais de préavis/pénalité peuvent s'appliquer selon la durée du contrat choisi."
              : "La session est payée d'avance et non remboursable une fois commencée. Toute absence demeure à ma charge."}</p>
            <p><strong className="text-white/90">Conduite.</strong> Respect des coachs, des partenaires et du tatami. Hygiène et tenue propre exigées à chaque cours.</p>
            <p><strong className="text-white/90">Risque & responsabilité.</strong> Les arts martiaux comportent des risques. Je m'engage à respecter les consignes de sécurité des instructeurs.</p>
            <p><strong className="text-white/90">Fréquence.</strong> Mon accès hebdomadaire correspond à la formule choisie (1× / 2× / 3× et + illimité).</p>
          </div>
          <label className="flex items-start gap-3 px-4 py-3 border-t border-white/10 text-[13px] text-white/85 cursor-pointer">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="checkout-accept-terms" className="w-4 h-4 mt-0.5 accent-[#D90429]" />
            <span>J'ai lu et j'accepte le contrat, les règles du dojo et les conditions de paiement et d'annulation.</span>
          </label>
        </div>

        {/* ── Décharge de responsabilité (signée en ligne) ── */}
        <div className="mt-4 border border-[#C9A961]/40 bg-[#C9A961]/[0.05]" data-testid="checkout-waiver-box">
          <p className="kz-mono text-[10px] tracking-[0.18em] uppercase text-[#C9A961] px-4 pt-4">🛡️ Décharge de responsabilité</p>
          <div className="px-4 pb-3 pt-2 text-[12px] leading-relaxed text-white/70">
            <p>Je reconnais que la pratique des arts martiaux comporte des risques de blessures. Je participe librement et à mes propres risques. Je dégage l'Institut Impact Élite Martial, ses instructeurs et ses représentants de toute responsabilité en cas de blessure, sauf faute lourde ou négligence grave. Pour un mineur, je signe à titre de parent / tuteur légal.</p>
          </div>
          <label className="flex items-start gap-3 px-4 py-3 border-t border-[#C9A961]/20 text-[13px] text-white/90 cursor-pointer">
            <input type="checkbox" checked={waiverAccepted} onChange={(e) => setWaiverAccepted(e.target.checked)} data-testid="checkout-accept-waiver" className="w-4 h-4 mt-0.5 accent-[#C9A961]" />
            <span>Je signe la décharge de responsabilité{form.is_minor ? " en tant que parent / tuteur légal" : ""}.</span>
          </label>
        </div>

        <button type="submit" disabled={busy || !accepted || !waiverAccepted} data-testid="checkout-pay-btn"
          className="w-full bg-[#D90429] text-white py-4 font-bold tracking-wider hover:bg-white hover:text-black transition-colors mt-7 flex items-center justify-center gap-2 disabled:opacity-50">
          {busy ? <CircleNotch size={18} className="animate-spin" /> : <><ShieldCheck size={18} weight="fill" /> Payer en sécurité</>}
        </button>
        <button type="button" onClick={onClose} data-testid="checkout-cancel-btn"
          className="w-full kz-mono text-[11px] tracking-[0.2em] uppercase text-white/40 hover:text-white transition-colors mt-3">
          Annuler
        </button>
        <p className="kz-body text-[11px] text-white/35 mt-4 leading-relaxed text-center">
          Tu seras redirigé vers la page de paiement sécurisée Stripe. Après le paiement, ton Espace Membre est activé automatiquement.
        </p>
      </form>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="kz-mono text-[10px] tracking-[0.18em] uppercase text-white/40">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="mt-1.5 w-full bg-transparent border-b border-white/20 focus:border-[#D90429] outline-none text-white py-2" />
    </label>
  );
}

/* ─────────────────────────── PAGE MERCI (polling) ───────────────────────── */
function AdhesionMerci({ sessionId }) {
  const [state, setState] = useState({ status: "checking", data: null });

  const poll = useCallback(async (attempt = 0) => {
    try {
      const r = await axios.get(`${API}/adhesion/status/${sessionId}`);
      const ps = r.data.payment_status;
      if (ps === "paid") {
        setState({ status: "paid", data: r.data });
        return;
      }
      if (r.data.session_status === "expired") {
        setState({ status: "expired", data: r.data });
        return;
      }
      if (attempt >= 6) {
        setState({ status: "pending", data: r.data });
        return;
      }
      setTimeout(() => poll(attempt + 1), 2500);
    } catch {
      if (attempt >= 6) { setState({ status: "error", data: null }); return; }
      setTimeout(() => poll(attempt + 1), 2500);
    }
  }, [sessionId]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div className="kz-body min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6 relative overflow-hidden" data-testid="adhesion-merci">
      <img src={ENSO} alt="" className="pointer-events-none absolute -right-20 -bottom-20 w-[420px] opacity-[0.05]" />
      <div className="w-full max-w-md text-center border border-white/10 bg-black/40 backdrop-blur-xl p-10">
        <img src={LOGO_WHITE} alt="Institut Impact Élite Martial" className="h-12 object-contain mx-auto" />

        {state.status === "checking" && (
          <>
            <CircleNotch size={40} className="text-[#D90429] animate-spin mx-auto mt-8" />
            <h1 className="kz-display text-2xl mt-6">Vérification du paiement…</h1>
            <p className="kz-body text-white/50 text-sm mt-2">Un instant, on confirme ta transaction.</p>
          </>
        )}

        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#D90429]/20 border-2 border-[#D90429] mx-auto flex items-center justify-center mt-8">
              <CheckCircle size={34} weight="fill" className="text-[#D90429]" />
            </div>
            <h1 className="kz-display text-3xl mt-6" data-testid="merci-paid-title">Bienvenue au dojo ! 🥋</h1>
            <p className="kz-body text-white/60 text-sm mt-3">
              Ton adhésion <b className="text-white">{state.data?.plan_label}</b> est confirmée. Ton Espace Membre est activé.
            </p>
            <p className="kz-body text-[11px] text-white/40 mt-2" data-testid="merci-receipt-note">Un reçu détaillé (abonnement + frais d'inscription 40 $ le cas échéant + taxes TPS/TVQ) t'a été envoyé par courriel via Stripe.</p>
            <Link to="/membres" data-testid="merci-portal-link"
              className="inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3.5 font-bold tracking-wider hover:bg-[#D90429] hover:text-white transition-colors mt-7 w-full">
              Accéder à mon Espace Membre <ArrowRight size={18} weight="bold" />
            </Link>
            <p className="kz-body text-[11px] text-white/35 mt-3">Connexion sans mot de passe avec ton courriel.</p>
          </>
        )}

        {(state.status === "pending" || state.status === "error") && (
          <>
            <CircleNotch size={36} className="text-white/40 mx-auto mt-8" />
            <h1 className="kz-display text-2xl mt-6">Paiement en traitement</h1>
            <p className="kz-body text-white/50 text-sm mt-2">
              Ton paiement est en cours de confirmation. Tu recevras un accès dès qu'il est validé.
            </p>
            <Link to="/membres" className="inline-block kz-mono text-[11px] tracking-[0.2em] uppercase text-[#D90429] hover:text-white transition-colors mt-6">
              Aller à l'Espace Membre →
            </Link>
          </>
        )}

        {state.status === "expired" && (
          <>
            <h1 className="kz-display text-2xl mt-8">Session expirée</h1>
            <p className="kz-body text-white/50 text-sm mt-2">Le paiement n'a pas été complété à temps.</p>
            <Link to="/adhesion" className="inline-block bg-[#D90429] text-white px-6 py-3 font-bold tracking-wider mt-6">
              Réessayer
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
